import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { BarChart3, TrendingUp, Users, FolderOpen, IndianRupee, Target, Clock, AlertCircle, CheckCircle } from "lucide-react";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function KpiCard({ label, value, icon: Icon, color = "blue", testId, onClick }) {
  const colors = {
    blue: "from-blue-500 to-blue-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
    violet: "from-violet-500 to-violet-600",
  };
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testId}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`p-5 rounded-2xl bg-gradient-to-br ${colors[color]} text-white shadow-sm transition-transform ${
        clickable ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm opacity-90">{label}</span>
        <Icon className="w-5 h-5 opacity-80" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function Bar({ label, value, max, color = "blue" }) {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-600 mb-1">
        <span className="truncate">{label}</span>
        <span className="font-semibold">{value}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full bg-${color}-500 rounded-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [kpi, setKpi] = useState({});
  const [rev, setRev] = useState(null);
  const [leads, setLeads] = useState(null);
  const [proj, setProj] = useState(null);

  const load = async () => {
    const [k, r, l, p] = await Promise.all([
      api.get("/admin/analytics/kpi"),
      api.get(`/admin/analytics/revenue?period=${period}`),
      api.get(`/admin/analytics/leads?period=${period}`),
      api.get(`/admin/analytics/projects`),
    ]);
    setKpi(k.data); setRev(r.data); setLeads(l.data); setProj(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const maxDay = Math.max(1, ...(rev?.by_day || []).map((d) => d.billed));
  const maxPkg = Math.max(1, ...(rev?.by_package || []).map((d) => d.amount));
  const maxSrc = Math.max(1, ...(leads?.by_source || []).map((d) => d.count));

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
          <BarChart3 className="w-6 h-6" /> Reports & Analytics
        </h2>
        <div className="flex gap-2" data-testid="period-tabs">
          {["today", "week", "month", "quarter"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${period === p ? "bg-[#1a3c5e] text-white" : "bg-white border border-slate-200 text-slate-700"}`}
              data-testid={`period-${p}`}
            >
              {p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Today Revenue" value={fmtINR(kpi.today_revenue)} icon={IndianRupee} color="emerald" testId="kpi-revenue" onClick={() => navigate("/admin/invoices")} />
        <KpiCard label="Today Leads" value={kpi.today_leads || 0} icon={Target} color="blue" testId="kpi-leads" onClick={() => navigate("/admin/leads")} />
        <KpiCard label="Active Projects" value={kpi.active_projects || 0} icon={FolderOpen} color="violet" testId="kpi-projects" onClick={() => navigate("/admin/projects")} />
        <KpiCard label="Hot Leads" value={kpi.hot_leads || 0} icon={TrendingUp} color="rose" testId="kpi-hot-leads" onClick={() => navigate("/admin/leads")} />
        <KpiCard label="Pending Payments" value={kpi.pending_payments || 0} icon={Clock} color="amber" testId="kpi-pending-pay" onClick={() => navigate("/admin/orders?status=pending_payment")} />
        <KpiCard label="Pending Approvals" value={kpi.pending_approvals || 0} icon={CheckCircle} color="amber" testId="kpi-pending-approvals" onClick={() => navigate("/admin/projects")} />
        <KpiCard label="Follow-Ups Due" value={kpi.follow_ups_due || 0} icon={AlertCircle} color="rose" testId="kpi-followups" onClick={() => navigate("/admin/follow-ups")} />
        <KpiCard label="Unread Chats" value={kpi.unread_conversations || 0} icon={Users} color="blue" testId="kpi-unread" onClick={() => navigate("/admin/inbox")} />
      </div>

      {/* Revenue */}
      {rev && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="revenue-card">
            <h3 className="font-semibold text-[#1a3c5e] mb-3">Revenue ({period})</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 rounded-lg bg-emerald-50">
                <div className="text-xs text-slate-600">Billed</div>
                <div className="font-bold text-emerald-700">{fmtINR(rev.total_billed)}</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-blue-50">
                <div className="text-xs text-slate-600">Collected</div>
                <div className="font-bold text-blue-700">{fmtINR(rev.total_collected)}</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-amber-50">
                <div className="text-xs text-slate-600">Outstanding</div>
                <div className="font-bold text-amber-700">{fmtINR(rev.outstanding)}</div>
              </div>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {(rev.by_day || []).map((d) => (
                <Bar key={d.date} label={d.date} value={d.billed} max={maxDay} color="emerald" />
              ))}
              {(rev.by_day || []).length === 0 && <div className="text-sm text-slate-500 text-center py-4">No data</div>}
            </div>
          </div>

          <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="package-card">
            <h3 className="font-semibold text-[#1a3c5e] mb-3">Revenue by Package</h3>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {(rev.by_package || []).map((p, i) => (
                <Bar key={i} label={`${p.title || p.slug} (${p.count})`} value={p.amount} max={maxPkg} color="blue" />
              ))}
              {(rev.by_package || []).length === 0 && <div className="text-sm text-slate-500 text-center py-4">No orders</div>}
            </div>
          </div>
        </div>
      )}

      {/* Leads */}
      {leads && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="leads-card">
            <h3 className="font-semibold text-[#1a3c5e] mb-3">Leads ({period})</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 rounded-lg bg-slate-50"><div className="text-xs text-slate-600">Total</div><div className="font-bold">{leads.total}</div></div>
              <div className="text-center p-3 rounded-lg bg-emerald-50"><div className="text-xs text-slate-600">Converted</div><div className="font-bold text-emerald-700">{leads.converted}</div></div>
              <div className="text-center p-3 rounded-lg bg-rose-50"><div className="text-xs text-slate-600">Lost</div><div className="font-bold text-rose-700">{leads.lost}</div></div>
            </div>
            <div className="text-sm text-slate-700 mb-3"><span className="font-semibold">Conversion Rate:</span> {leads.conversion_rate}%</div>
            <div className="space-y-2">
              {(leads.by_source || []).map((s, i) => (
                <Bar key={i} label={s.source} value={s.count} max={maxSrc} color="violet" />
              ))}
            </div>
          </div>

          <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="sales-perf-card">
            <h3 className="font-semibold text-[#1a3c5e] mb-3">Sales Executive Performance</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="pb-2">Sales</th><th>Leads</th><th>Conv.</th><th>Rate</th>
              </tr></thead>
              <tbody>
                {(leads.sales_performance || []).map((s, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2">{s.user?.name || "—"}</td>
                    <td>{s.total_leads}</td>
                    <td className="text-emerald-700 font-semibold">{s.converted}</td>
                    <td>{s.conversion_rate}%</td>
                  </tr>
                ))}
                {(leads.sales_performance || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center py-4 text-slate-500">No sales activity</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Projects */}
      {proj && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="projects-card">
            <h3 className="font-semibold text-[#1a3c5e] mb-3">Projects Overview</h3>
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="text-center p-3 rounded-lg bg-slate-50"><div className="text-xs">Total</div><div className="font-bold">{proj.total}</div></div>
              <div className="text-center p-3 rounded-lg bg-blue-50"><div className="text-xs">Active</div><div className="font-bold text-blue-700">{proj.active}</div></div>
              <div className="text-center p-3 rounded-lg bg-emerald-50"><div className="text-xs">Done</div><div className="font-bold text-emerald-700">{proj.completed}</div></div>
              <div className="text-center p-3 rounded-lg bg-rose-50"><div className="text-xs">Delayed</div><div className="font-bold text-rose-700">{proj.delayed}</div></div>
            </div>
            <div className="text-sm text-slate-700"><span className="font-semibold">Avg Delivery:</span> {proj.avg_delivery_days ?? "—"} days</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {(proj.by_stage || []).map((s, i) => (
                <div key={i} className="p-2 bg-slate-50 rounded text-xs flex justify-between">
                  <span className="text-slate-600">{s.stage}</span><span className="font-semibold">{s.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="designer-perf-card">
            <h3 className="font-semibold text-[#1a3c5e] mb-3">Designer Performance</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="pb-2">Designer</th><th>Active</th><th>Done</th><th>Total</th>
              </tr></thead>
              <tbody>
                {(proj.designer_performance || []).slice(0, 10).map((d, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2">{d.user?.name || "—"}</td>
                    <td>{d.active}</td>
                    <td className="text-emerald-700 font-semibold">{d.completed}</td>
                    <td>{d.total}</td>
                  </tr>
                ))}
                {(proj.designer_performance || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center py-4 text-slate-500">No designers active</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
